import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList } from 'recharts';
import Card from '../components/Card';
import CustomerHistoryModal from '../components/CustomerHistoryModal';
import { getCustomersMonthly, getCustomersDispatched } from '../services/dashboardApi';

// CUSTDISP v2: month-wise number of customers dispatched; click a month -> its customers; click a customer -> history
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const lbl = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;
const tick = { fill: 'var(--color-text-secondary)', fontSize: 11 };
const TH = { textAlign: 'left', verticalAlign: 'bottom', padding: '8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'middle' };
const TF = { ...TD, fontWeight: 800, borderTop: '2px solid var(--color-border)' };
const FIELD = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', background: 'var(--color-surface)', color: 'var(--color-text-primary)' };
const HINT = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 };

export default function CustomersDispatchedPage() {
  const navigate = useNavigate();
  const [months, setMonths] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState('');
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null);

  useEffect(() => {
    let alive = true;
    getCustomersMonthly()
      .then((d) => { if (alive) { setMonths(d.months); setSel(d.months[d.months.length - 1].ym); } })
      .catch(() => { if (alive) setErr('Could not load.'); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!sel) return undefined;
    let alive = true;
    setList(null); setQ('');
    getCustomersDispatched(sel).then((d) => { if (alive) setList(d); }).catch(() => { if (alive) setList({ items: [], label: sel, count: 0, totalBoxes: 0 }); });
    return () => { alive = false; };
  }, [sel]);

  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (list ? list.items : []).filter((i) => !s || `${i.name} ${i.city || ''}`.toLowerCase().includes(s));
  }, [list, q]);

  const chart = (months || []).map((m) => ({ ym: m.ym, label: lbl(m.ym), Customers: m.customers }));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <h1 style={{ margin: 0 }}>Number of Customers Dispatched</h1>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>Back to Dashboard</button>
      </div>

      {err && <Card><p style={{ margin: 0, color: 'var(--color-danger)' }}>{err}</p></Card>}
      {!months && !err && <Card><p style={{ margin: 0, color: 'var(--color-text-muted)' }}>Loading…</p></Card>}

      {months && (
        <>
          <Card title="Month-wise customers (last 12 months)" style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ width: '100%', height: 280, cursor: 'pointer' }}>
              <ResponsiveContainer>
                <BarChart data={chart} margin={{ top: 18, right: 8, left: -8, bottom: 0 }}
                  onClick={(st) => { if (st && st.activeTooltipIndex != null && chart[st.activeTooltipIndex]) setSel(chart[st.activeTooltipIndex].ym); }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={{ stroke: 'var(--color-border)' }} interval={0} />
                  <YAxis tick={tick} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip formatter={(v) => [fmt(v), 'Customers']} contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="Customers" radius={[3, 3, 0, 0]} maxBarSize={36}>
                    {chart.map((c) => <Cell key={c.ym} fill={c.ym === sel ? '#B25C3F' : '#0E6F8A'} />)}
                    <LabelList dataKey="Customers" position="top" style={{ fill: 'var(--color-text-secondary)', fontSize: 11 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={HINT}>Click a month to see which customers took dispatch.</div>
          </Card>

          <Card title="Month summary" style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={TH}>Month</th><th style={{ ...TH, textAlign: 'right' }}>Customers dispatched</th><th style={{ ...TH, textAlign: 'right' }}>Box</th></tr></thead>
                <tbody>
                  {months.slice().reverse().map((m) => (
                    <tr key={m.ym} style={{ cursor: 'pointer', background: m.ym === sel ? 'var(--color-accent-soft)' : undefined }} onClick={() => setSel(m.ym)}>
                      <td style={{ ...TD, fontWeight: 600 }}>{lbl(m.ym)}</td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{fmt(m.customers)}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{fmt(m.boxes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title={list ? `${list.label} — ${fmt(list.count)} customers` : 'Customers'} style={{ marginBottom: 'var(--space-4)' }}>
            {!list && <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>Loading…</p>}
            {list && (
              <>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / city" style={{ ...FIELD, width: '100%', maxWidth: 320, marginBottom: 8 }} />
                <div style={{ overflowX: 'auto', maxHeight: 520, overflowY: 'auto' }} className="table-scroll">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={TH}>#</th><th style={TH}>Customer</th><th style={TH}>City</th><th style={{ ...TH, textAlign: 'right' }}>Dispatched Box</th></tr></thead>
                    <tbody>
                      {items.length === 0 && <tr><td style={TD} colSpan={4}>No customer found.</td></tr>}
                      {items.map((i, idx) => (
                        <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => setActive({ id: i.id, name: i.name, from: list.from, to: list.to })}>
                          <td style={TD}>{idx + 1}</td>
                          <td style={{ ...TD, fontWeight: 600 }}>{i.name}</td>
                          <td style={TD}>{i.city || '—'}</td>
                          <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{fmt(i.boxes)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {items.length > 0 && (
                      <tfoot><tr><td style={TF} colSpan={3}>Total ({items.length} customers)</td><td style={{ ...TF, textAlign: 'right' }}>{fmt(items.reduce((t, i) => t + i.boxes, 0))}</td></tr></tfoot>
                    )}
                  </table>
                </div>
                <div style={HINT}>Click a customer to see that customer&apos;s dispatch history.</div>
              </>
            )}
          </Card>
        </>
      )}
      {active && <CustomerHistoryModal customer={active} onClose={() => setActive(null)} />}
    </div>
  );
}
